import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Plus, Pencil, Trash2, Search, Brain, Loader2 } from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

const getRiskLevel = (score) => {
  if (score >= 70) return { label: "Alto", class: "high", color: "#FF3B30" };
  if (score >= 40) return { label: "Medio", class: "medium", color: "#FF9500" };
  return { label: "Basso", class: "low", color: "#34C759" };
};

const Clients = () => {
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [analyzingClient, setAnalyzingClient] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    total_credit: "",
    days_overdue: ""
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, getAuthHeaders());
      setClients(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        total_credit: parseFloat(formData.total_credit) || 0,
        days_overdue: parseInt(formData.days_overdue) || 0
      };

      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, data, getAuthHeaders());
        toast.success("Cliente aggiornato");
      } else {
        await axios.post(`${API}/clients`, data, getAuthHeaders());
        toast.success("Cliente aggiunto");
      }

      setIsDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/clients/${id}`, getAuthHeaders());
      toast.success("Cliente eliminato");
      fetchClients();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone || "",
      total_credit: client.total_credit?.toString() || "",
      days_overdue: client.days_overdue?.toString() || ""
    });
    setIsDialogOpen(true);
  };

  const handleAnalyzeRisk = async (clientId) => {
    setAnalyzingClient(clientId);
    try {
      const response = await axios.post(
        `${API}/clients/${clientId}/analyze-risk`,
        {},
        {
          ...getAuthHeaders(),
          timeout: 60000  // 60 seconds timeout for AI analysis
        }
      );
      toast.success("Analisi completata");
      fetchClients();
    } catch (error) {
      console.error("AI Analysis error:", error);
      toast.error("Errore nell'analisi AI: " + (error.message || "timeout"));
    } finally {
      setAnalyzingClient(null);
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      total_credit: "",
      days_overdue: ""
    });
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="clients-page">
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Clienti</h1>
          <p className="page-subtitle">Gestione clienti e analisi rischio AI</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full px-6"
              data-testid="add-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-2xl border-0 shadow-2xl sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-[#1D1D1F]">
                {editingClient ? "Modifica Cliente" : "Nuovo Cliente"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-[#1D1D1F]">Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  className="h-12 rounded-xl border-[#D2D2D7]"
                  data-testid="client-name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@esempio.it"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="client-email"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Telefono</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+39 123 456 7890"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="client-phone"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Credito Totale (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.total_credit}
                    onChange={(e) => setFormData({ ...formData, total_credit: e.target.value })}
                    placeholder="0,00"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="client-credit"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1D1D1F]">Giorni Ritardo</Label>
                  <Input
                    type="number"
                    value={formData.days_overdue}
                    onChange={(e) => setFormData({ ...formData, days_overdue: e.target.value })}
                    placeholder="0"
                    className="h-12 rounded-xl border-[#D2D2D7]"
                    data-testid="client-overdue"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                  className="flex-1 h-12 rounded-full border-[#D2D2D7]"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
                  data-testid="save-client-btn"
                >
                  {editingClient ? "Aggiorna" : "Salva"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="pt-6">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868B]" />
            <Input
              placeholder="Cerca clienti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11 rounded-xl border-[#D2D2D7]"
              data-testid="search-clients"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <CardContent className="p-0">
          <Table data-testid="clients-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Cliente
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Contatti
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Credito
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-center">
                  Ritardo
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50">
                  Rischio AI
                </TableHead>
                <TableHead className="h-14 px-6 text-xs font-semibold uppercase tracking-wider text-[#86868B] bg-[#F5F5F7]/50 text-right">
                  Azioni
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => {
                  const riskScore = client.risk_score ?? (client.days_overdue * 2 + (client.total_credit / 1000));
                  const risk = getRiskLevel(riskScore);
                  
                  return (
                    <TableRow key={client.id} className="hover:bg-[#F5F5F7]/30">
                      <TableCell className="px-6 py-4">
                        <p className="font-medium text-[#1D1D1F]">{client.name}</p>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <p className="text-[#1D1D1F]">{client.email}</p>
                        {client.phone && (
                          <p className="text-sm text-[#86868B]">{client.phone}</p>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right font-semibold text-[#1D1D1F]">
                        {formatCurrency(client.total_credit || 0)}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          client.days_overdue > 60 ? 'bg-[#FF3B30]/10 text-[#FF3B30]' :
                          client.days_overdue > 30 ? 'bg-[#FF9500]/10 text-[#FF9500]' :
                          'bg-[#34C759]/10 text-[#34C759]'
                        }`}>
                          {client.days_overdue || 0} gg
                        </span>
                      </TableCell>
                      <TableCell className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-24">
                            <Progress 
                              value={Math.min(riskScore, 100)} 
                              className="h-2"
                              style={{ 
                                '--progress-background': risk.color 
                              }}
                            />
                          </div>
                          <span className={`risk-badge ${risk.class}`}>
                            {Math.round(riskScore)}%
                          </span>
                        </div>
                        {client.risk_analysis && (
                          <p className="text-xs text-[#86868B] mt-1 max-w-[200px] truncate">
                            {client.risk_analysis}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAnalyzeRisk(client.id)}
                            disabled={analyzingClient === client.id}
                            className="h-9 w-9 rounded-lg hover:bg-[#AF52DE]/10"
                            data-testid={`analyze-client-${client.id}`}
                            title="Analisi AI"
                          >
                            {analyzingClient === client.id ? (
                              <Loader2 className="w-4 h-4 text-[#AF52DE] animate-spin" />
                            ) : (
                              <Brain className="w-4 h-4 text-[#AF52DE]" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(client)}
                            className="h-9 w-9 rounded-lg hover:bg-[#007AFF]/10"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Pencil className="w-4 h-4 text-[#007AFF]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(client.id)}
                            className="h-9 w-9 rounded-lg hover:bg-[#FF3B30]/10"
                            data-testid={`delete-client-${client.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#FF3B30]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center text-[#86868B]">
                    Nessun cliente trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Clients;
